import React, { useState, useEffect, useContext } from 'react';
import { apiService } from '../services/apiService';
import { v4 as uuidv4 } from 'uuid';
import { refreshDataContext } from '../context/AppContext';

function EditScheduleModal({ isOpen, onClose, classLink, scheduleId, isNew = false }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lessons, setLessons] = useState([]);
  const refreshData = useContext(refreshDataContext);
  const [formData, setFormData] = useState({
    classLink: classLink || '',
    scheduleTitle: '',
    age: '',
    day: '',
    startHour: '',
    endHour: '',
    location: '',
    price: 0
  });

  // Days mapping for dropdown
  const days = {
    1: "Maandag",
    2: "Dinsdag",
    3: "Woensdag",
    4: "Donderdag",
    5: "Vrijdag",
    6: "Zaterdag",
    7: "Zondag",
  };

  // Locations for dropdown (you might want to fetch this from an API)
  const locations = ["TMDC","DIA"];

  useEffect(() => {
    // Fetch all lessons if we're creating a new schedule and no classLink is provided
    if (isOpen && isNew) {
      if (classLink) {
        // If classLink is provided, just initialize with empty values
        setFormData({
          classLink: classLink,
          scheduleTitle: '',
          age: '',
          day: '',
          startHour: '',
          endHour: '',
          location: '',
          price: 0
        });
        setLoading(false);
      } else {
        // If no classLink is provided, we need to fetch available lessons
        fetchLessons();
      }
    }
    // For existing schedules, fetch the data
    else if (isOpen && classLink && scheduleId) {
      fetchLessonData();
    }
    // eslint-disable-next-line
  }, [isOpen, classLink, scheduleId, isNew]);
  
  const fetchLessons = async () => {
    setLoading(true);
    try {
      // Fetch lessons from API
      const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/lessons`);
      if (!response.ok) throw new Error('Failed to fetch lessons');
      
      const data = await response.json();
      setLessons(data);
      
      // If there's only one lesson, select it automatically
      if (data.length === 1) {
        setFormData(prev => ({
          ...prev,
          classLink: data[0].link
        }));
      }
      
      setLoading(false);
    } catch (err) {
      console.error("Error fetching lessons:", err);
      setError("Failed to load lessons. Please try again.");
      setLoading(false);
    }
  };

  const fetchLessonData = async () => {
    setLoading(true);
    try {
      const response = await apiService.getSchedule(classLink, scheduleId);

      // Set form data with the fetched schedule
      setFormData({
        classLink: response.classLink || '',
        scheduleTitle: response.scheduleTitle || '',
        age: response.age || '',
        day: response.day || '',
        startHour: response.startHour || '',
        endHour: response.endHour || '',
        location: response.location || '',
        price: response.price || 0
      });
      setLoading(false);
    } catch (err) {
      console.error("Error fetching schedule data:", err);
      setError("Failed to load lesson data. Please try again.");
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'price' ? parseInt(value, 10) || 0 : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (isNew) {
        // Generate a new scheduleId if creating a new schedule
        const newScheduleId = uuidv4();
        await apiService.createSchedule(formData['classLink'], newScheduleId, formData);
      } else {
        // Update existing schedule
        await apiService.updateSchedule(classLink, scheduleId, formData);
      }
      
      // Refresh the data in the context
      refreshData();
      
      setLoading(false);
      onClose(true); // Close modal and indicate success
    } catch (err) {
      console.error(`Error ${isNew ? 'creating' : 'updating'} schedule:`, err);
      setError(`Failed to ${isNew ? 'create' : 'update'} lesson. Please try again.`);
      setLoading(false);
    }
  };
  
  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this schedule?')) {
      setLoading(true);
      try {
        await apiService.deleteSchedule(classLink, scheduleId);
        
        // Refresh the data in the context
        refreshData();
        
        setLoading(false);
        onClose(true); // Close modal and indicate success
      } catch (err) {
        console.error("Error deleting schedule:", err);
        setError("Failed to delete lesson. Please try again.");
        setLoading(false);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div className="modal-content" style={{
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '5px',
        width: '90%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        <div className="modal-header">
          <h3>{isNew ? 'Add New Lesson Schedule' : 'Edit Lesson Schedule'}</h3>
          <button 
            type="button" 
            className="btn btn-close me-auto" 
            onClick={() => onClose(false)}
            aria-label="Close"
          > X </button>
        </div>
        
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}
        
        {loading ? (
          <div className="text-center my-4">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {isNew && !classLink && lessons.length > 0 && (
              <div className="mb-3">
                <label htmlFor="classLink" className="form-label">Lesson Type</label>
                <select
                  className="custom-select"
                  id="classLink"
                  name="classLink"
                  value={formData.classLink}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select a lesson type</option>
                  {lessons.map((lesson) => (
                    <option key={lesson.link} value={lesson.link}>
                      {lesson.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            <div className="mb-3">
              <label htmlFor="scheduleTitle" className="form-label">Schedule Title</label>
              <input
                type="text"
                className="form-control"
                id="scheduleTitle"
                name="scheduleTitle"
                value={formData.scheduleTitle}
                onChange={handleChange}
                required
              />
            </div>
            
            <div className="mb-3">
              <label htmlFor="age" className="form-label">Age Group</label>
              <input
                type="text"
                className="form-control"
                id="age"
                name="age"
                value={formData.age}
                onChange={handleChange}
                required
              />
            </div>
            
            <div className="mb-3">
              <label htmlFor="day" className="form-label">Day</label>
              <select
                className="custom-select"
                id="day"
                name="day"
                value={formData.day}
                onChange={handleChange}
                required
              >
                <option value="">Select a day</option>
                {Object.entries(days).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="row">
              <div className="col-md-6 mb-3">
                <label htmlFor="startHour" className="form-label">Start Time</label>
                <input
                  type="text"
                  className="form-control"
                  id="startHour"
                  name="startHour"
                  value={formData.startHour}
                  onChange={handleChange}
                  placeholder="e.g. 18:00"
                  required
                />
              </div>
              
              <div className="col-md-6 mb-3">
                <label htmlFor="endHour" className="form-label">End Time</label>
                <input
                  type="text"
                  className="form-control"
                  id="endHour"
                  name="endHour"
                  value={formData.endHour}
                  onChange={handleChange}
                  placeholder="e.g. 19:00"
                  required
                />
              </div>
            </div>
            
            <div className="mb-3">
              <label htmlFor="location" className="form-label">Location</label>
              <select
                className="custom-select"
                id="location"
                name="location"
                value={formData.location}
                onChange={handleChange}
                required
              >
                <option value="">Select a location</option>
                {locations.map((location) => (
                  <option key={location} value={location}>
                    {location}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="mb-3">
              <label htmlFor="price" className="form-label">Price (€)</label>
              <input
                type="number"
                className="form-control"
                id="price"
                name="price"
                value={formData.price}
                onChange={handleChange}
                min="0"
                required
              />
            </div>
            
            <div className="modal-footer">
              {!isNew && (
                <button 
                  type="button" 
                  className="btn btn-danger me-auto" 
                  onClick={handleDelete}
                  disabled={loading}
                >
                  Delete
                </button>
              )}
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={() => onClose(false)}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? 'Saving...' : (isNew ? 'Create' : 'Save Changes')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default EditScheduleModal;